import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, Layers, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Skeleton } from '@/components/insights/Skeleton';
import { BlueprintPlan, BlueprintRegistry, blueprintsService } from '@/services/blueprints.service';
import { familyIcon } from '@/pages/blueprints/blueprint-meta';
import { timeAgo } from '@/pages/industry-configs/industry-meta';
import { BlueprintPlanSummary } from './BlueprintPlanSummary';

/**
 * Company → Details: which blueprint shapes this company's CRM, which version
 * it last received, and what re-applying would add now.
 */
export function CompanyBlueprintCard({ companyUid, industry }: { companyUid: string; industry?: string | null }) {
  const [plan, setPlan] = useState<BlueprintPlan | null>(null);
  const [registry, setRegistry] = useState<BlueprintRegistry | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([blueprintsService.companyPlan(companyUid), blueprintsService.registry()])
      .then(([p, r]) => { setPlan(p); setRegistry(r); })
      .catch(() => { /* interceptor toasts */ });
  }, [companyUid]);
  // The industry can change from the card next to this one.
  useEffect(() => { load(); }, [load, industry]);

  const apply = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const r = await blueprintsService.apply(companyUid);
      setPlan(r);
      const n = r.result ? Object.values(r.result).reduce((a, b) => a + b, 0) : 0;
      toast.success(n ? `${r.blueprint?.name} blueprint applied — ${n} item${n === 1 ? '' : 's'} added` : 'Already up to date');
    } catch { /* interceptor toasts */ } finally { setBusy(false); }
  };

  if (!plan) return <Skeleton className="h-40 w-full rounded-2xl" />;

  if (!plan.blueprint) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-5 shadow-soft">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-surface-alt text-text-muted"><Layers size={18} /></span>
          <div>
            <h3 className="text-sm font-semibold text-text-primary">Standard CRM</h3>
            <p className="mt-0.5 text-xs text-text-muted">
              {industry ? 'This industry has no blueprint, so the company uses the standard pages and words.' : 'Assign an industry to see whether a blueprint applies.'}{' '}
              <Link to="/blueprints" className="font-medium text-primary hover:underline">Blueprints</Link>
            </p>
          </div>
        </div>
      </section>
    );
  }

  const Icon = familyIcon(plan.blueprint.familyKey);
  const behind = plan.applied && plan.applied.version < plan.blueprint.version;
  return (
    <section className="rounded-2xl border border-border bg-surface shadow-soft">
      <header className="flex flex-wrap items-start gap-3 border-b border-border px-5 py-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary"><Icon size={19} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-text-primary">{plan.blueprint.name} blueprint</h3>
            <Link to={`/blueprints/${plan.blueprint.familyKey}`} className="text-2xs font-medium text-primary hover:underline">Edit</Link>
          </div>
          <p className="mt-0.5 text-xs text-text-muted">
            {plan.applied
              ? <>Received v{plan.applied.version} {timeAgo(plan.applied.appliedAt)} · latest is v{plan.blueprint.version}</>
              : 'Not applied yet — pages and words already follow it.'}
          </p>
        </div>
        {plan.upToDate ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2.5 py-1 text-2xs font-semibold text-success">
            <CheckCircle2 size={12} /> Up to date
          </span>
        ) : (
          <Button size="sm" iconLeft={behind ? <Sparkles size={14} /> : <RefreshCw size={14} />} loading={busy} onClick={() => setConfirm(true)}>
            {plan.applied ? 'Add what’s new' : 'Apply blueprint'}
          </Button>
        )}
      </header>
      <div className="p-5">
        {plan.upToDate ? (
          <p className="text-xs text-text-muted">Everything in the blueprint is already in this company. Anything they renamed or removed stays their way.</p>
        ) : (
          <>
            <p className="mb-3 text-xs text-text-muted">Applying adds only these — nothing the company changed or deleted is touched.</p>
            <BlueprintPlanSummary plan={plan} registry={registry} mode="update" />
          </>
        )}
      </div>
      <ConfirmDialog
        open={confirm}
        title={`Add the ${plan.blueprint.name} blueprint items?`}
        message="New fields, stages, departments and bookable services are added to this company. Existing ones — including anything the company renamed or deleted — are left alone."
        onConfirm={apply}
        onCancel={() => setConfirm(false)}
      />
    </section>
  );
}
